import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DynamicFields } from "@/components/DynamicFields";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { euro, formatDate, MEZZI, PREVENTIVO_VOCI, SOLUZIONE_VOLO_SCHEMA, SOLUZIONE_HOTEL_SCHEMA } from "@/lib/constants";
import { Pencil, Trash2, MapPin, Plane, Building2, Car, Ticket, Receipt, Route, ArrowLeft, Check, X, Printer, Map as MapIcon, ExternalLink, Send, Search, RefreshCw, Ban, CheckCircle2, Plus, Layers } from "lucide-react";

const mezzoLabel = (v) => MEZZI.find((m) => m.value === v)?.label || v;

function Info({ label, value }) {
  if (value === undefined || value === null || value === "" || value === false) return null;
  return (
    <div className="py-1">
      <dt className="text-xs text-slate-400">{label}</dt>
      <dd className="text-sm text-slate-800">{value === true ? "Sì" : String(value)}</dd>
    </div>
  );
}

export default function TripDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);

  const load = useCallback(() => api.get(`/trips/${id}`).then((r) => setTrip(r.data)).catch((e) => { toast.error(apiError(e)); navigate("/trasferte"); }), [id, navigate]);
  useEffect(() => { load(); }, [load]);

  if (!trip) return <Layout><div className="text-slate-500">Caricamento...</div></Layout>;

  const del = async () => {
    try { await api.delete(`/trips/${id}`); toast.success("Trasferta eliminata"); navigate("/trasferte"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const hotel = trip.hotel || {};
  const prev = trip.preventivo || {};

  return (
    <Layout>
      <button onClick={() => navigate("/trasferte")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="back-btn">
        <ArrowLeft size={16} /> Torna alle trasferte
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2">
              <MapPin className="text-[#E30613]" /> {trip.destinazione || "Trasferta"}
            </h1>
            <StatusBadge stato={trip.stato} testid="detail-status" />
          </div>
          <p className="text-slate-500 mt-1">
            {trip.dipendente} · {trip.azienda} · {formatDate(trip.data_partenza)} → {formatDate(trip.data_rientro)}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="print-pdf-btn" onClick={() => window.open(`/trasferte/${id}/stampa`, "_blank")}>
            <Printer size={15} className="mr-1.5" /> Scarica PDF
          </Button>
          <Button variant="outline" data-testid="edit-trip-btn" onClick={() => navigate(`/trasferte/${id}/modifica`)}>
            <Pencil size={15} className="mr-1.5" /> Modifica
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" data-testid="delete-trip-btn" className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 size={15} /></Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminare la trasferta?</AlertDialogTitle>
                <AlertDialogDescription>L'operazione non è reversibile.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annulla</AlertDialogCancel>
                <AlertDialogAction data-testid="confirm-delete" onClick={del} className="bg-rose-600 hover:bg-rose-700">Elimina</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <WorkflowPanel trip={trip} user={user} onUpdate={setTrip} />

      {trip.workflow_diagnostics?.length > 0 && (
        <div data-testid="legacy-workflow-warning" className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
          <p className="font-semibold">Storico workflow incompleto — dato legacy</p>
          <ul className="mt-1 list-disc pl-5 text-sm">{trip.workflow_diagnostics.map((issue) => <li key={issue}>{issue}</li>)}</ul>
        </div>
      )}
      <Tabs defaultValue="itinerario">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="itinerario" data-testid="tab-itinerario"><Route size={15} className="mr-1.5" /> Itinerario</TabsTrigger>
          <TabsTrigger value="soluzioni" data-testid="tab-soluzioni"><Layers size={15} className="mr-1.5" /> Soluzioni</TabsTrigger>
          <TabsTrigger value="trasporti" data-testid="tab-trasporti"><Plane size={15} className="mr-1.5" /> Trasporti</TabsTrigger>
          <TabsTrigger value="alloggio" data-testid="tab-alloggio"><Building2 size={15} className="mr-1.5" /> Alloggio</TabsTrigger>
          <TabsTrigger value="mappe" data-testid="tab-mappe"><MapIcon size={15} className="mr-1.5" /> Mappe</TabsTrigger>
          <TabsTrigger value="prenotazioni" data-testid="tab-prenotazioni"><Ticket size={15} className="mr-1.5" /> Prenotazioni</TabsTrigger>
          <TabsTrigger value="preventivo" data-testid="tab-preventivo"><Receipt size={15} className="mr-1.5" /> Preventivo</TabsTrigger>
        </TabsList>

        {/* ITINERARIO */}
        <TabsContent value="itinerario">
          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="itinerary-view">
            <h3 className="font-display text-xl font-semibold mb-1">Itinerario completo</h3>
            <p className="text-sm text-slate-500 mb-6">{trip.motivo}</p>
            <div className="relative border-l-2 border-slate-200 pl-6 space-y-6">
              <TimelineItem title={`Partenza — ${trip.destinazione}`} sub={`${formatDate(trip.data_partenza)} ${trip.ora_partenza || ""}`} />
              {trip.trasporti.map((s, i) => (
                <TimelineItem key={s.id || i} title={`${mezzoLabel(s.mezzo)}`} sub={`${s.dettagli?.luogo_partenza || s.dettagli?.stazione_partenza || s.dettagli?.aeroporto_partenza || s.da || ""} → ${s.dettagli?.luogo_arrivo || s.dettagli?.stazione_arrivo || s.dettagli?.aeroporto_arrivo || s.a || ""}`} />
              ))}
              {hotel.struttura && <TimelineItem title={`Hotel — ${hotel.struttura}`} sub={`${hotel.numero_notti || ""} notti · ${hotel.indirizzo || ""}`} />}
              {trip.destinazione_lavoro?.nome && <TimelineItem title={`Destinazione — ${trip.destinazione_lavoro.nome}`} sub={trip.destinazione_lavoro.indirizzo} />}
              {trip.trasporti_locali.map((s, i) => (
                <TimelineItem key={i} title={`Trasferimento locale — ${s.mezzo || ""}`} sub={`${s.da || ""} → ${s.a || ""}`} />
              ))}
              <TimelineItem title="Rientro" sub={`${formatDate(trip.data_rientro)} ${trip.ora_rientro || ""}`} last />
            </div>

            {trip.informazioni_consigli && Object.values(trip.informazioni_consigli).some(Boolean) && (
              <div className="mt-8 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-amber-900 mb-2 text-sm">Informazioni e consigli</h4>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                  {Object.entries(trip.informazioni_consigli).filter(([, v]) => v).map(([k, v]) => (
                    <Info key={k} label={k.replace(/_/g, " ")} value={v} />
                  ))}
                </dl>
              </div>
            )}
          </div>

          {trip.approvazioni?.length > 0 && (
            <div className="mt-6"><ResearchHistory trip={trip} /></div>
          )}
        </TabsContent>

        {/* SOLUZIONI */}
        <TabsContent value="soluzioni">
          <SolutionsTab trip={trip} user={user} onUpdate={setTrip} />
        </TabsContent>

        {/* TRASPORTI */}
        <TabsContent value="trasporti">
          <div className="space-y-4">
            {trip.trasporti.length === 0 && <Empty text="Nessun segmento di trasporto." />}
            {trip.trasporti.map((s, i) => (
              <div key={s.id || i} className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`detail-segment-${i}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="h-6 w-6 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center">{i + 1}</span>
                  <span className="font-semibold text-slate-900">{mezzoLabel(s.mezzo)}</span>
                </div>
                <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6">
                  {Object.entries(s.dettagli || {}).filter(([, v]) => v !== "" && v !== null).map(([k, v]) => (
                    <Info key={k} label={k.replace(/_/g, " ")} value={k === "costo" ? euro(v) : v} />
                  ))}
                </dl>
              </div>
            ))}
            {trip.trasporti_locali.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-lg p-5">
                <h4 className="font-semibold mb-3 text-sm">Trasporti locali</h4>
                {trip.trasporti_locali.map((s, i) => (
                  <div key={i} className="text-sm text-slate-700 border-b border-slate-50 py-2">{s.da} → {s.a} · {s.mezzo} {s.costo ? `· ${euro(s.costo)}` : ""}</div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ALLOGGIO */}
        <TabsContent value="alloggio">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <h4 className="font-semibold mb-3 flex items-center gap-2"><Building2 size={16} className="text-[#E30613]" /> Hotel / Pernottamento</h4>
              {hotel.struttura || hotel.tipo ? (
                <dl className="grid grid-cols-2 gap-x-6">
                  {Object.entries(hotel).filter(([, v]) => v !== "" && v !== null && v !== false).map(([k, v]) => (
                    <Info key={k} label={k.replace(/_/g, " ")} value={k === "costo" ? euro(v) : v} />
                  ))}
                </dl>
              ) : <Empty text="Nessun pernottamento." />}
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <h4 className="font-semibold mb-3 flex items-center gap-2"><Car size={16} className="text-[#E30613]" /> Parcheggi</h4>
              {trip.parcheggi.length === 0 ? <Empty text="Nessun parcheggio." /> : trip.parcheggi.map((p, i) => (
                <div key={i} className="border-b border-slate-50 py-2 text-sm text-slate-700">{p.nome || p.destinazione} {p.costo ? `· ${euro(p.costo)}` : ""}</div>
              ))}
            </div>
            {trip.destinazione_lavoro?.nome && (
              <div className="bg-white border border-slate-200 rounded-lg p-5 lg:col-span-2">
                <h4 className="font-semibold mb-3 flex items-center gap-2"><MapPin size={16} className="text-[#E30613]" /> Destinazione di lavoro</h4>
                <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6">
                  {Object.entries(trip.destinazione_lavoro).filter(([, v]) => v !== "" && v !== null && v !== false).map(([k, v]) => (
                    <Info key={k} label={k.replace(/_/g, " ")} value={v} />
                  ))}
                </dl>
              </div>
            )}
          </div>
        </TabsContent>

        {/* MAPPE */}
        <TabsContent value="mappe">
          <MapsView trip={trip} hotel={hotel} />
        </TabsContent>

        {/* PRENOTAZIONI */}
        <TabsContent value="prenotazioni">
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            {trip.prenotazioni.length === 0 ? <div className="p-10 text-center"><Empty text="Nessuna prenotazione." /></div> : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  <tr><th className="text-left px-4 py-3">Tipo</th><th className="text-left px-4 py-3">Fornitore</th><th className="text-left px-4 py-3">Codice</th><th className="text-left px-4 py-3">Data</th><th className="text-right px-4 py-3">Costo</th></tr>
                </thead>
                <tbody>
                  {trip.prenotazioni.map((b, i) => (
                    <tr key={i} className="border-t border-slate-50" data-testid={`detail-booking-${i}`}>
                      <td className="px-4 py-3 font-medium">{b.tipo}</td>
                      <td className="px-4 py-3">{b.fornitore || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs">{b.codice || "—"}</td>
                      <td className="px-4 py-3">{formatDate(b.data)}</td>
                      <td className="px-4 py-3 text-right">{euro(b.costo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/* PREVENTIVO */}
        <TabsContent value="preventivo">
          <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-xl" data-testid="preventivo-view">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-semibold">Preventivo trasferta</h3>
            </div>
            <dl className="divide-y divide-slate-100">
              {PREVENTIVO_VOCI.map((v) => (
                <div key={v.key} className="flex justify-between py-2.5 text-sm">
                  <dt className="text-slate-600">{v.label}</dt>
                  <dd className="text-slate-900 font-medium">{euro(prev.voci?.[v.key])}</dd>
                </div>
              ))}
              <div className="flex justify-between py-4">
                <dt className="font-display text-lg font-bold text-slate-900">Totale trasferta</dt>
                <dd className="font-display text-lg font-bold text-[#E30613]" data-testid="preventivo-total">{euro(prev.totale)}</dd>
              </div>
            </dl>
          </div>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}

function TimelineItem({ title, sub, last }) {
  return (
    <div className="relative">
      <span className={`absolute -left-[31px] top-1 h-3.5 w-3.5 rounded-full border-2 border-white ${last ? "bg-[#E30613]" : "bg-slate-900"}`} />
      <div className="font-medium text-slate-900">{title}</div>
      {sub && <div className="text-sm text-slate-500">{sub}</div>}
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-sm text-slate-400">{text}</p>;
}

const AZIONE_LABEL = {
  invia_richiesta: "Richiesta inviata",
  avvia_ricerca: "Ricerca avviata",
  proponi_soluzioni: "Soluzioni proposte",
  invia_approvazione: "Inviata in approvazione",
  approva: "Soluzione approvata",
  richiedi_nuova_ricerca: "Nuova ricerca richiesta",
  prenota: "Prenotata",
  completa_itinerario: "Itinerario completato",
  annulla: "Trasferta annullata",
};
const AZIONE_ICON = {
  invia_richiesta: Send,
  avvia_ricerca: Search,
  proponi_soluzioni: Layers,
  invia_approvazione: Send,
  approva: CheckCircle2,
  richiedi_nuova_ricerca: RefreshCw,
  prenota: CheckCircle2,
  completa_itinerario: Route,
  annulla: Ban,
};

function FlightComparison({ voli, canApprove, onApprove }) {
  const minPrice = useMemo(() => {
    const prices = voli.map((v) => Number(v.dettagli?.prezzo || 0)).filter((n) => n > 0);
    return prices.length ? Math.min(...prices) : 0;
  }, [voli]);
  const rows = [
    { label: "Compagnia", get: (d) => d.compagnia },
    { label: "Aeroporto partenza", get: (d) => d.aeroporto_partenza },
    { label: "Aeroporto arrivo", get: (d) => d.aeroporto_arrivo },
    { label: "Orario", get: (d) => `${d.ora_partenza || "—"} → ${d.ora_arrivo || "—"}` },
    { label: "Volo diretto", diretto: true },
    { label: "Bagaglio", get: (d) => d.bagaglio },
    { label: "Prezzo", price: true },
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-6" data-testid="comparison-table">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        <Plane size={16} className="text-[#E30613]" />
        <h4 className="font-semibold text-slate-900 text-sm">Confronto voli — {voli.length} alternative</h4>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left px-4 py-2.5 font-medium text-slate-500 sticky left-0 bg-slate-50 min-w-[140px]">Voce</th>
              {voli.map((v, i) => (
                <th key={v.id} className="px-4 py-2.5 text-left min-w-[160px]" data-testid={`cmp-col-${i}`}>
                  <div className="flex items-center gap-2 font-semibold text-slate-900">
                    {v.etichetta}
                    {v.selezionata && <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-300 rounded-full px-1.5 py-0.5">Approvata</span>}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri} className="border-t border-slate-50">
                <td className="px-4 py-2.5 text-slate-500 sticky left-0 bg-white">{r.label}</td>
                {voli.map((v) => {
                  const d = v.dettagli || {};
                  if (r.diretto) {
                    return <td key={v.id} className="px-4 py-2.5">{d.diretto ? <span className="text-emerald-700 font-medium">Diretto</span> : <span className="text-slate-600">{d.scali ? `Scalo: ${d.scali}` : "Con scalo"}</span>}</td>;
                  }
                  if (r.price) {
                    const p = Number(d.prezzo || 0);
                    const cheapest = p > 0 && p === minPrice;
                    return <td key={v.id} className={`px-4 py-2.5 ${cheapest ? "text-emerald-700 font-bold" : "text-slate-900 font-medium"}`}>{euro(d.prezzo)}{cheapest && <span className="ml-1 text-[10px] font-semibold text-emerald-600">più economico</span>}</td>;
                  }
                  return <td key={v.id} className="px-4 py-2.5 text-slate-700">{r.get(d) || "—"}</td>;
                })}
              </tr>
            ))}
            {canApprove && (
              <tr className="border-t border-slate-100 bg-slate-50/50">
                <td className="px-4 py-3 sticky left-0 bg-slate-50/50" />
                {voli.map((v, i) => (
                  <td key={v.id} className="px-4 py-3">
                    {v.selezionata ? (
                      <span className="text-xs text-emerald-700 font-semibold flex items-center gap-1"><Check size={13} /> Approvata</span>
                    ) : (
                      <Button size="sm" data-testid={`cmp-approve-${i}`} onClick={() => onApprove(v.id)} className="bg-emerald-600 hover:bg-emerald-700 h-8"><Check size={13} className="mr-1" /> Approva</Button>
                    )}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ResearchHistory({ trip }) {
  const events = trip.approvazioni || [];
  const rounds = useMemo(() => {
    if (!events.length) return [];
    const grouped = [];
    let current = { reason: null, events: [] };
    events.forEach((event) => {
      current.events.push(event);
      if (event.azione === "richiedi_nuova_ricerca") {
        grouped.push(current);
        current = { reason: event.nota, events: [] };
      }
    });
    grouped.push(current);
    return grouped;
  }, [events]);
  if (!events.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="research-history">
      <h4 className="font-display text-lg font-semibold text-slate-900 mb-4">Storico ricerche e trattativa</h4>
      <div className="space-y-5">
        {rounds.map((r, i) => (
          <div key={i} data-testid={`research-round-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="h-6 w-6 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center">{i + 1}</span>
              <span className="font-semibold text-sm text-slate-900">Ricerca #{i + 1}{i === 0 ? " · Iniziale" : ""}</span>
            </div>
            {r.reason && (
              <div className="ml-8 mb-2 text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2 text-amber-800">
                <strong>Motivo nuova ricerca:</strong> {r.reason}
              </div>
            )}
            <div className="ml-8 border-l-2 border-slate-100 pl-4 space-y-2">
              {r.events.map((e) => {
                const Icon = AZIONE_ICON[e.azione] || Check;
                return (
                  <div key={e.id} className="flex items-center justify-between text-sm gap-3">
                    <span className="flex items-center gap-2 min-w-0">
                      <Icon size={14} className="text-[#E30613] shrink-0" />
                      <span className="text-slate-800">{AZIONE_LABEL[e.azione] || e.azione}</span>
                      <span className="text-slate-400 truncate">· {e.da} ({e.ruolo})</span>
                      {e.azione === "richiedi_nuova_ricerca" && e.nota && <span className="text-amber-700 truncate">— "{e.nota}"</span>}
                    </span>
                    <span className="text-slate-400 shrink-0">{formatDate(e.data)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const WF_HINT = {
  bozza: "Bozza non ancora inviata. Invia la richiesta per avviare il processo.",
  richiesta: "Richiesta inviata. In attesa che l'amministratore avvii la ricerca.",
  ricerca_in_corso: "Ricerca in corso da parte dell'amministratore.",
  soluzioni_proposte: "Soluzioni inserite. Possono essere inviate al responsabile per l'approvazione.",
  in_approvazione: "In attesa di approvazione: approvare una soluzione (scheda Soluzioni) o richiedere una nuova ricerca.",
  approvata: "Soluzione approvata. Procedere con la prenotazione.",
  prenotata: "Prenotazione effettuata. Completare l'itinerario finale.",
  itinerario_finale: "Itinerario finale completato.",
  annullata: "Trasferta annullata.",
};

function WorkflowPanel({ trip, user, onUpdate }) {
  const [reason, setReason] = useState("");
  const [showReason, setShowReason] = useState(false);
  const role = user.role;
  const stato = trip.stato;
  const isOwner = trip.created_by === user.id;

  const doTransition = async (action, extra = {}) => {
    try {
      const r = await api.post(`/trips/${trip.id}/transition`, { action, ...extra });
      onUpdate(r.data);
      toast.success("Stato aggiornato");
      setShowReason(false); setReason("");
    } catch (e) { toast.error(apiError(e)); }
  };

  const actions = [];
  if (stato === "bozza" && (role !== "dipendente" || isOwner)) actions.push({ key: "invia_richiesta", label: "Invia richiesta", icon: Send, cls: "bg-[#E30613] hover:bg-[#C50510]" });
  if (role === "amministratore") {
    if (stato === "richiesta") actions.push({ key: "avvia_ricerca", label: "Avvia ricerca", icon: Search, cls: "bg-[#E30613] hover:bg-[#C50510]" });
    if (stato === "ricerca_in_corso") actions.push({ key: "proponi_soluzioni", label: "Proponi soluzioni", icon: Send, cls: "bg-[#E30613] hover:bg-[#C50510]" });
    if (stato === "soluzioni_proposte") { actions.push({ key: "invia_approvazione", label: "Invia al responsabile", icon: Send, cls: "bg-[#E30613] hover:bg-[#C50510]" }); actions.push({ key: "avvia_ricerca", label: "Riapri ricerca", icon: Search, variant: "outline" }); }
    if (stato === "approvata") actions.push({ key: "prenota", label: "Segna come prenotata", icon: CheckCircle2, cls: "bg-emerald-600 hover:bg-emerald-700" });
    if (stato === "prenotata") actions.push({ key: "completa_itinerario", label: "Completa itinerario", icon: Route, cls: "bg-indigo-600 hover:bg-indigo-700" });
  }
  const canRequestResearch = (role === "responsabile" || role === "amministratore") && stato === "in_approvazione";
  const canCancel = stato !== "annullata" && stato !== "itinerario_finale" && (role !== "dipendente" || isOwner);
  const lastResearch = [...(trip.approvazioni || [])].reverse().find((a) => a.azione === "richiedi_nuova_ricerca");

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6" data-testid="workflow-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900">Flusso di lavoro</h3>
          <p className="text-sm text-slate-500 mt-0.5">{WF_HINT[stato]}</p>
        </div>
        <StatusBadge stato={stato} testid="workflow-status" />
      </div>

      {stato === "ricerca_in_corso" && lastResearch && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800" data-testid="research-reason">
          <span className="font-medium">Nuova ricerca richiesta da {lastResearch.da}:</span> {lastResearch.nota}
        </div>
      )}

      {(actions.length > 0 || canRequestResearch || canCancel) && (
        <div className="flex gap-2 flex-wrap mt-4">
          {actions.map((a) => {
            const Icon = a.icon;
            return (
              <Button key={a.key} size="sm" variant={a.variant || "default"} className={a.cls || ""} data-testid={`wf-${a.key}`} onClick={() => doTransition(a.key)}>
                <Icon size={15} className="mr-1.5" /> {a.label}
              </Button>
            );
          })}
          {canRequestResearch && (
            <Button size="sm" variant="outline" data-testid="wf-richiedi_nuova_ricerca" onClick={() => setShowReason(true)}>
              <RefreshCw size={15} className="mr-1.5" /> Richiedi nuova ricerca
            </Button>
          )}
          {canCancel && (
            <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="wf-annulla" onClick={() => doTransition("annulla")}>
              <Ban size={15} className="mr-1.5" /> Annulla trasferta
            </Button>
          )}
        </div>
      )}

      {canRequestResearch && (
        <p className="text-xs text-slate-400 mt-3">Per approvare una soluzione apri la scheda <strong>Soluzioni</strong> e conferma quella scelta.</p>
      )}

      <Dialog open={showReason} onOpenChange={setShowReason}>
        <DialogContent>
          <DialogHeader><DialogTitle>Richiedi una nuova ricerca</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Motivo della richiesta</Label>
            <Textarea data-testid="research-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Es. Troppo costoso · Cercare un volo dopo le 08:00 · Soluzione più economica" />
            <div className="flex flex-wrap gap-1.5 pt-1">
              {["Troppo costoso", "Cercare un volo più tardi", "Soluzione più economica", "Orario non adatto"].map((s) => (
                <button key={s} type="button" onClick={() => setReason(s)} className="text-xs border border-slate-200 rounded-full px-2.5 py-1 hover:border-[#E30613]">{s}</button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReason(false)}>Annulla</Button>
            <Button data-testid="submit-research-btn" onClick={() => doTransition("richiedi_nuova_ricerca", { nota: reason })} className="bg-[#E30613] hover:bg-[#C50510]">Invia richiesta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SolutionsTab({ trip, user, onUpdate }) {
  const role = user.role;
  const isAdmin = role === "amministratore";
  const canApprove = (role === "responsabile" || role === "amministratore") && trip.stato === "in_approvazione" && trip.created_by !== user.id;
  const canAdd = isAdmin && ["ricerca_in_corso", "soluzioni_proposte"].includes(trip.stato);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tipo, setTipo] = useState("volo");
  const [dettagli, setDettagli] = useState({});
  const soluzioni = trip.soluzioni || [];
  const voli = soluzioni.filter((s) => s.tipo === "volo");

  const openNew = () => { setEditing(null); setTipo("volo"); setDettagli({}); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setTipo(s.tipo); setDettagli(s.dettagli || {}); setOpen(true); };

  const save = async () => {
    try {
      const body = { tipo, dettagli };
      const r = editing
        ? await api.put(`/trips/${trip.id}/solutions/${editing.id}`, body)
        : await api.post(`/trips/${trip.id}/solutions`, body);
      onUpdate(r.data); setOpen(false); toast.success("Soluzione salvata");
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (sid) => { try { const r = await api.delete(`/trips/${trip.id}/solutions/${sid}`); onUpdate(r.data); toast.success("Soluzione eliminata"); } catch (e) { toast.error(apiError(e)); } };
  const approve = async (sid) => { try { const r = await api.post(`/trips/${trip.id}/transition`, { action: "approva", solution_id: sid }); onUpdate(r.data); toast.success("Soluzione approvata"); } catch (e) { toast.error(apiError(e)); } };

  const schema = tipo === "hotel" ? SOLUZIONE_HOTEL_SCHEMA : SOLUZIONE_VOLO_SCHEMA;

  return (
    <div data-testid="solutions-tab">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">Alternative proposte per il confronto ({soluzioni.length}). Il responsabile ne approva una.</p>
        {canAdd && <Button data-testid="add-solution-btn" onClick={openNew} className="bg-[#E30613] hover:bg-[#C50510]"><Plus size={16} className="mr-1.5" /> Aggiungi soluzione</Button>}
      </div>

      {voli.length >= 2 && <FlightComparison voli={voli} canApprove={canApprove} onApprove={approve} />}

      {soluzioni.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-10 text-center"><Empty text="Nessuna soluzione proposta al momento." /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {soluzioni.map((s, i) => (
            <div key={s.id} data-testid={`solution-${i}`} className={`bg-white border rounded-lg p-5 ${s.selezionata ? "border-emerald-400 ring-1 ring-emerald-300" : "border-slate-200"}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 font-semibold text-slate-900">
                  {s.tipo === "hotel" ? <Building2 size={16} className="text-[#E30613]" /> : <Plane size={16} className="text-[#E30613]" />}
                  {s.etichetta}
                </div>
                {s.selezionata && <span className="text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-300 rounded-full px-2.5 py-0.5">Approvata</span>}
              </div>
              <dl className="grid grid-cols-2 gap-x-4">
                {Object.entries(s.dettagli || {}).filter(([, v]) => v !== "" && v !== null && v !== false).map(([k, v]) => (
                  <Info key={k} label={k.replace(/_/g, " ")} value={k === "prezzo" ? euro(v) : v} />
                ))}
              </dl>
              <div className="flex gap-2 mt-4">
                {canApprove && <Button size="sm" data-testid={`approve-solution-${i}`} onClick={() => approve(s.id)} className="bg-emerald-600 hover:bg-emerald-700"><Check size={14} className="mr-1" /> Approva questa soluzione</Button>}
                {canAdd && <Button size="sm" variant="outline" data-testid={`edit-solution-${i}`} onClick={() => openEdit(s)}><Pencil size={14} /></Button>}
                {canAdd && <Button size="sm" variant="outline" className="text-rose-600 border-rose-200" data-testid={`delete-solution-${i}`} onClick={() => del(s.id)}><Trash2 size={14} /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica soluzione" : "Nuova soluzione proposta"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5 max-w-xs">
              <Label>Tipo soluzione</Label>
              <Select value={tipo} onValueChange={setTipo}>
                <SelectTrigger data-testid="solution-tipo"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="volo">Volo</SelectItem><SelectItem value="hotel">Hotel</SelectItem></SelectContent>
              </Select>
            </div>
            <DynamicFields schema={schema} values={dettagli} onChange={(k, v) => setDettagli((d) => ({ ...d, [k]: v }))} idPrefix="sol" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-solution-btn" onClick={save} className="bg-[#E30613] hover:bg-[#C50510]">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {trip.approvazioni?.length > 0 && (
        <div className="mt-6"><ResearchHistory trip={trip} /></div>
      )}
    </div>
  );
}

function MapsView({ trip, hotel }) {
  const dest = trip.destinazione_lavoro || {};
  const points = [];
  if (dest.indirizzo || dest.nome) points.push({ label: `Destinazione — ${dest.nome || dest.tipologia || ""}`, addr: dest.indirizzo || dest.nome });
  if (hotel.indirizzo || hotel.struttura) points.push({ label: `Hotel — ${hotel.struttura || ""}`, addr: hotel.indirizzo || hotel.struttura });
  (trip.parcheggi || []).forEach((p, i) => {
    if (p.indirizzo || p.nome) points.push({ label: `Parcheggio — ${p.nome || p.destinazione || i + 1}`, addr: p.indirizzo || p.nome });
  });
  (trip.trasporti || []).forEach((s) => {
    const a = s.dettagli?.aeroporto_arrivo || s.dettagli?.aeroporto_partenza;
    if (a) points.push({ label: `Aeroporto — ${a}`, addr: a });
  });
  if (trip.destinazione && !points.some((p) => p.addr === trip.destinazione)) {
    points.unshift({ label: `Destinazione trasferta`, addr: trip.destinazione });
  }

  if (points.length === 0) {
    return <div className="bg-white border border-slate-200 rounded-lg p-10 text-center"><Empty text="Nessun indirizzo disponibile. Aggiungi indirizzi a hotel, destinazione o parcheggi." /></div>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="maps-view">
      {points.map((p) => (
        <div key={p.label} className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm" data-testid={`map-card-${p.label}`}>
          <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
            <div>
              <div className="font-medium text-slate-900 text-sm">{p.label}</div>
              <div className="text-xs text-slate-500">{p.addr}</div>
            </div>
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.addr)}`} target="_blank" rel="noreferrer" className="text-[#E30613] shrink-0" data-testid={`map-open-${p.label}`}>
              <ExternalLink size={16} />
            </a>
          </div>
          <iframe
            title={p.label}
            className="w-full h-56"
            loading="lazy"
            src={`https://maps.google.com/maps?q=${encodeURIComponent(p.addr)}&output=embed`}
          />
        </div>
      ))}
    </div>
  );
}
